import { html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { ApiService } from '@trp-dms-digital-shared/ds-ui-library-api';
import { PopApiBaseComponent } from '../../base/pop-api-base-component';
import { Constants } from './characteristics-table.constants';
import { Constants as BaseConstants } from '../../base/base.constants.js';
import type {
  CharacteristicsData,
  Characteristics,
} from './characteristics-table.model';
import { dateShort } from '../../common/utils/formatDate';
import { sanitizeHTML } from '../../common/utils/sanitizeHTML';
import { isNullish } from '../../common/utils/nullish';
import { CharacteristicsFragments } from '../../common/fragments/characteristics.fragments';
import {
  formatCharacteristicsValue,
  isCharacteristicsWithRank,
} from './characteristics-table.utils';
import {
  ensureParagraphTag,
  toSentenceCase,
} from '../../common/utils/textFormat';
import { generateLocalDisclaimerText } from '../../common/utils/generateDisclaimer';
import style from './characteristics-table.scss?inline';

/**
 * @tag pop-characteristics-table-component
 * @property {CharacteristicsData} [componentObject] Characteristics table content
 * @attribute {CharacteristicsData} [data-component-object] Characteristics table content
 */
@customElement(Constants.TAG_NAME)
export class PopCharacteristicsTable extends PopApiBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * The component object that contains the pre-fetched data to be rendered.
   */
  @property({
    attribute: BaseConstants.DATA_COMPONENT_OBJECT,
    type: Object,
  })
  componentObject?: CharacteristicsData;

  /**
   * @internal
   */
  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  componentName = PopCharacteristicsTable.tagName;

  /**
   * @internal
   */
  fragment = CharacteristicsFragments.characteristicsFragment;

  constructor() {
    super(new ApiService());
  }

  protected processResponse(_response): void {
    this.componentObject = {
      ..._response?.yieldsDividendsExhibit,
      productName: _response?.metadata?.portfolioName,
    };
  }

  generateTooltipId(title: string) {
    return title.toLowerCase().replace(/ /g, '-');
  }

  renderTooltipTrigger(title: string) {
    const tooltipTriggerLabel = `${title} details`;
    return html`
      <beacon-icon-button
        id="${this.generateTooltipId(title)}-button"
        slot="action-zone-right"
        variant="tertiary"
      >
        ${tooltipTriggerLabel}
        <beacon-icon slot="icon" name="information"></beacon-icon>
      </beacon-icon-button>
    `;
  }

  /**
   * Renders the table head for the characteristics table component.
   */
  private renderTableHead() {
    return html`
      <tr>
        <th id="name" scope="col">
          <beacon-table-header variant="jumbo"> </beacon-table-header>
        </th>
        <th id="product" scope="col">
          <beacon-table-header variant="jumbo"
            >${this.componentObject?.productName}</beacon-table-header
          >
        </th>
        ${this.isBenchmarkColumnVisible()
          ? html` <th id="benchmark" scope="col">
              <beacon-table-header variant="jumbo"
                >${Constants.BENCHMARK_COLUMN_LABEL}</beacon-table-header
              >
            </th>`
          : null}
      </tr>
    `;
  }

  private getFormattedRowTitle(title: string) {
    return toSentenceCase(title);
  }

  /**
   * Renders the table body for the characteristics table component.
   * @returns {TemplateResult} The rendered table body.
   */
  private renderTableBody(characteristics: Characteristics[]) {
    if (!characteristics || characteristics.length === 0) {
      return html`
        <tr>
          <td colspan="3">${Constants.NO_DATA_MESSAGE}</td>
        </tr>
      `;
    }

    return html`
      ${characteristics.map((row) => {
        const value = !isNullish(row.value)
          ? formatCharacteristicsValue(row.value, row.valueFormat)
          : Constants.NOT_APPLICABLE;

        const benchmarkValue = !isNullish(row.benchmarkValue)
          ? formatCharacteristicsValue(row.benchmarkValue, row.valueFormat)
          : Constants.NOT_APPLICABLE;

        const formattedRowTitle = this.getFormattedRowTitle(row.title);

        return html`
          <tr>
            <th scope="row">
              <beacon-table-stub>
                <span
                  slot="title"
                  class="b-typography-sans-serif--body--100 b-font-weight-bold"
                >
                  ${formattedRowTitle}
                </span>
                ${this.renderTooltipTrigger(formattedRowTitle)}
              </beacon-table-stub>
            </th>
            <td>
              <beacon-table-record variant="numeral">
                ${value}
              </beacon-table-record>
            </td>
            ${this.isBenchmarkColumnVisible()
              ? html` <td>
                  <beacon-table-record variant="numeral">
                    ${benchmarkValue}
                  </beacon-table-record>
                </td>`
              : null}
          </tr>
        `;
      })}
    `;
  }

  isBenchmarkColumnVisible() {
    if (!this.componentObject?.benchmarkName) {
      return false;
    }

    return (
      this.componentObject?.portfolioCharacteristics?.some(
        (characteristic) => !isNullish(characteristic.benchmarkValue),
      ) ?? false
    );
  }

  isDataAvailable() {
    return (
      this.componentObject?.portfolioCharacteristics?.some(
        (characteristic) => !isNullish(characteristic.value),
      ) ?? false
    );
  }

  createPopoverContent(characteristic: Characteristics) {
    const tooltipContent = characteristic.tooltip
      ? characteristic.tooltip
      : Constants.NO_DATA_MESSAGE;

    return html`
      <beacon-popover
        opens-by="#${this.generateTooltipId(characteristic.title)}-button"
      >
        ${unsafeHTML(sanitizeHTML(ensureParagraphTag(tooltipContent)))}
      </beacon-popover>
    `;
  }

  generatePopovers(characteristics: Characteristics[]) {
    if (characteristics.length > 0) {
      return html`
        ${characteristics.map((characteristic) => {
          return this.createPopoverContent(characteristic);
        })}
      `;
    }
    return html``;
  }

  renderCharacteristicsTable() {
    const filteredCharacteristics =
      this.componentObject?.portfolioCharacteristics
        ?.filter((characteristic) => characteristic.title)
        .filter(isCharacteristicsWithRank)
        .sort((a, b) => a.rank - b.rank);

    return html`
      <div class="characteristics-table">
        <div class="popover-layer">
          ${this.generatePopovers(filteredCharacteristics)}
        </div>
        <beacon-table variant="fixed">
          <table>
            <thead>
              ${this.renderTableHead()}
            </thead>
            <tbody>
              ${this.renderTableBody(filteredCharacteristics)}
            </tbody>
          </table>
        </beacon-table>
      </div>
    `;
  }

  renderBenchmarkName() {
    return this.isBenchmarkColumnVisible()
      ? html`<div
          class="b-typography-sans-serif--caption--300 b-margin-block-start--micro--jumbo benchmark-label"
        >
          <span
            class="b-typography-sans-serif--caption--300 b-font-weight-bold "
            >${Constants.BENCHMARK_NAME_LABEL} </span
          >${this.componentObject?.benchmarkName}
        </div>`
      : null;
  }

  renderAsOfDate() {
    if (this.componentObject?.asOfDate) {
      const text = `${Constants.AS_OF_DATE_PREFIX} ${dateShort(this.componentObject?.asOfDate)}`;
      return html`<div class="as-of-date">
        <p class="b-typography-sans-serif--caption-loose--300">${text}</p>
      </div>`;
    }
    return html``;
  }

  renderTitle() {
    return html`
      <h3
        class="b-typography-serif--headline--100 b-font-weight-bold characteristics-title"
      >
        ${Constants.COMPONENT_TITLE}
      </h3>
    `;
  }

  private renderDisclosures() {
    const disclaimers = this.componentObject?.disclaimers;
    if (!disclaimers || disclaimers.length === 0) {
      return html``;
    }

    const disclaimerText = generateLocalDisclaimerText(disclaimers);

    return html`<div class="b-margin-block-start--micro--jumbo">
      <pop-disclosure-component data-footnote-indicator-type="symbol">
        ${unsafeHTML(sanitizeHTML(disclaimerText))}
      </pop-disclosure-component>
    </div>`;
  }

  render() {
    if (!this.componentObject) {
      return html``;
    }
    if (!this.isDataAvailable()) {
      return this.renderComponent(
        html` <div class="b-container">
          ${this.renderTitle()}
          <pop-no-data-available-component
            data-message="${Constants.COMPONENT_UNAVAILABLE}"
          ></pop-no-data-available-component>
        </div>`,
      );
    }
    return this.renderComponent(html`
      <div class="b-container">
        ${this.renderTitle()} ${this.renderAsOfDate()}
        ${this.renderCharacteristicsTable()} ${this.renderBenchmarkName()}
        ${this.renderDisclosures()}
      </div>
    `);
  }
}
